/**
 * 统一模型定义接口
 * - 每个模型的 index.ts 必须导出此格式的对象
 */
import type { GenerationAdapter } from '@/client/adapters/types';
import type {
  ModelConfigEntry,
  CanvasPortSpec,
  ModelPollingConfig,
  ModelCopyLocalization,
  ModelSeoConfig,
  VersionOption,
  ModelRole,
  VideoGenModeConfig,
} from '@/client/config/models.config';
import type { ProviderId } from '@/client/config/providers.config';
import type { ModelWorkspaceConfig } from '@/features/workspace/modelFormTypes';

// 重新导出 ModelRole 以便其他模块使用
export type { ModelRole } from '@/client/config/models.config';

export interface ModelDefinition {
  /** 模型唯一标识(kebab-case) */
  modelKey: string;
  /** 所属 Provider ID */
  providerId: ProviderId;
  /** 分组 ID(可选,默认等于 providerId) */
  groupId?: string;
  /** 显示名称 */
  label: string;
  /** 模型描述 */
  description?: string;
  /** 别名列表 */
  aliases?: string[];
  /** 路由段(可选,默认等于 modelKey) */
  routeSegment?: string;
  /** 默认任务类型 */
  defaultTaskType: 'text' | 'image' | 'video' | 'audio' | 'multimodal';
  /** UI 模式 */
  mode: 'text' | 'image' | 'video';
  /** 是否在 UI 中可见(默认 true) */
  visible?: boolean;
  /** 模型角色：generator = 生成模型，processor = 处理节点（默认 generator） */
  role?: ModelRole;
  /** API 适配器实例 */
  adapter: GenerationAdapter;
  /** 请求默认配置 */
  defaults?: Record<string, unknown>;
  /** 版本选项列表 */
  versions?: VersionOption[];
  /** Workspace 配置 */
  workspace?: ModelWorkspaceConfig;
  /** Canvas 端口规范 */
  canvasPortSpec?: CanvasPortSpec;
  /**
   * 可选：按版本（通常为表单字段 `model`）提供差异化 canvas 端口规范
   * - key 为版本值
   * - 支持提供 `default` 作为兜底
   */
  canvasPortSpecByVersion?: Record<string, CanvasPortSpec>;
  /** 轮询配置 */
  polling?: ModelPollingConfig;
  /** SEO 配置 */
  seo?: ModelSeoConfig;
  /** 国际化配置 */
  i18n?: ModelCopyLocalization;
  /** 显式节点端口配置(可选) */
  nodeGraphPorts?: ModelConfigEntry['nodeGraphPorts'];
  /** 视频生成模式配置（仅适用于支持图片输入的视频模型） */
  videoGenMode?: VideoGenModeConfig;
}

/**
 * 类型守卫:检查对象是否为有效的 ModelDefinition
 */
export function isValidModelDefinition(obj: unknown): obj is ModelDefinition {
  if (!obj || typeof obj !== 'object') return false;
  const def = obj as Partial<ModelDefinition>;

  return !!(
    typeof def.modelKey === 'string' &&
    typeof def.providerId === 'string' &&
    typeof def.label === 'string' &&
    typeof def.defaultTaskType === 'string' &&
    typeof def.mode === 'string' &&
    def.adapter &&
    typeof def.adapter.generate === 'function'
  );
}

/**
 * 将 ModelDefinition 转换为 ModelConfigEntry
 */
export function toModelConfigEntry(def: ModelDefinition): ModelConfigEntry {
  return {
    providerId: def.providerId,
    groupId: def.groupId,
    label: def.label,
    modelKey: def.modelKey,
    defaultTaskType: def.defaultTaskType,
    mode: def.mode,
    adapter: def.adapter,
    defaults: def.defaults,
    visible: def.visible,
    role: def.role ?? 'generator', // 默认为 generator，保持向后兼容
    description: def.description,
    aliases: def.aliases,
    versions: def.versions,
    routeSegment: def.routeSegment,
    workspace: def.workspace,
    polling: def.polling,
    seo: def.seo,
    canvasPortSpec: def.canvasPortSpec,
    canvasPortSpecByVersion: def.canvasPortSpecByVersion,
    i18n: def.i18n,
    nodeGraphPorts: def.nodeGraphPorts,
    videoGenMode: def.videoGenMode,
  };
}
